import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { assessments, cases } from '../services/api';
import ChatPanel from '../components/chat/ChatPanel';
import EvidencePanel from '../components/assessment/EvidencePanel';
import TimerBar from '../components/assessment/TimerBar';
import ActorPanel from '../components/assessment/ActorPanel';
import ActorForm from '../components/assessment/ActorForm';
import ACHMatrix from '../components/assessment/ACHMatrix';
import NotesPanel from '../components/assessment/NotesPanel';
import ThreatAssessmentForm from '../components/assessment/ThreatAssessmentForm';
import TimeExpiredModal from '../components/assessment/TimeExpiredModal';
import MarkdownViewer from '../components/common/MarkdownViewer';
import AvisCopia from '../components/common/AvisCopia';
import { useProteccioCopia } from '../utils/proteccioCopia';

const TABS = [
  { id: 'case', label: 'Cas', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { id: 'actors', label: 'Actors', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  { id: 'ach', label: 'ACH', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
  { id: 'assessment', label: 'Avaluació', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' }
];

function AssessmentWorkspacePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { connected, activeUsers, joinAssessment, leaveAssessment, on } = useSocket();

  // UI state
  const [activeTab, setActiveTab] = useState('case');
  const [chatOpen, setChatOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [evidenceCollapsed, setEvidenceCollapsed] = useState(false);
  const [showActorForm, setShowActorForm] = useState(false);
  const [editingActor, setEditingActor] = useState(null);
  const [showTimeExpired, setShowTimeExpired] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  // Copiar i enganxar queden bloquejats per als alumnes —les dues menes, els
  // grups amb codi d'examen i els analistes amb compte propi, que a la base de
  // dades són tots `analyst`—. Instructors i administradors no es toquen.
  // El perquè i els límits, a utils/proteccioCopia.js.
  const avisCopia = useProteccioCopia(user?.role === 'analyst');

  // Data state
  const [assessment, setAssessment] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [actorTemplates, setActorTemplates] = useState([]);
  const [actors, setActors] = useState([]);
  const [hypotheses, setHypotheses] = useState([]);
  const [achCells, setAchCells] = useState([]);
  const [notes, setNotes] = useState([]);
  const [threatAssessment, setThreatAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isReadOnly = !['in_progress'].includes(assessment?.status);

  // Load all assessment data
  const loadAssessment = useCallback(async () => {
    try {
      setLoading(true);
      const [
        assessmentRes,
        actorsRes,
        achRes,
        notesRes,
        threatRes
      ] = await Promise.all([
        assessments.getById(id),
        assessments.getActors(id),
        assessments.getACH(id),
        assessments.getNotes(id),
        assessments.getThreatAssessment(id)
      ]);

      let assessmentData = assessmentRes.data;

      // If assessment is in not_started state (e.g., after reset), auto-start it
      if (assessmentData.status === 'not_started') {
        try {
          const teamId = assessmentData.team?.id;
          await cases.startAssessment(assessmentData.caseId, teamId ? { teamId } : {});
          // Reload assessment data after starting
          const updatedAssessment = await assessments.getById(id);
          assessmentData = updatedAssessment.data;
          // Also reload actors since they may have been created on start
          const updatedActors = await assessments.getActors(id);
          setActors(updatedActors.data);
        } catch (startErr) {
          console.warn('Could not auto-start assessment:', startErr);
          // Fallback to initially loaded actors
          setActors(actorsRes.data);
        }
      } else {
        setActors(actorsRes.data);
      }

      setAssessment(assessmentData);
      setHypotheses(achRes.data.hypotheses || []);
      setAchCells(achRes.data.cells || []);
      setNotes(notesRes.data);
      setThreatAssessment(threatRes.data);

      // Load case-level data (evidence, templates)
      if (assessmentData.caseId) {
        const [evidenceRes, templatesRes] = await Promise.all([
          cases.getEvidence(assessmentData.caseId),
          cases.getActorTemplates(assessmentData.caseId)
        ]);
        setEvidence(evidenceRes.data);
        setActorTemplates(templatesRes.data);
      }

      // Check if time expired
      if (assessmentData.status === 'time_expired') {
        setShowTimeExpired(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant avaluació');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  // Join/leave socket room
  useEffect(() => {
    if (assessment && connected) {
      joinAssessment(id);
      return () => leaveAssessment(id);
    }
  }, [assessment, connected, id, joinAssessment, leaveAssessment]);

  // Listen for real-time updates
  useEffect(() => {
    if (!connected) return;

    const handlers = {
      // Actors - check for duplicates before adding
      actor_created: (actor) => setActors(prev =>
        prev.some(a => a.id === actor.id) ? prev : [...prev, actor]
      ),
      actor_updated: (actor) => setActors(prev => prev.map(a => a.id === actor.id ? actor : a)),
      actor_deleted: ({ actorId }) => setActors(prev => prev.filter(a => a.id !== actorId)),
      // Hypotheses - check for duplicates before adding
      hypothesis_created: (h) => setHypotheses(prev =>
        prev.some(x => x.id === h.id) ? prev : [...prev, h]
      ),
      hypothesis_updated: (h) => setHypotheses(prev => prev.map(x => x.id === h.id ? h : x)),
      hypothesis_deleted: ({ hypothesisId }) => setHypotheses(prev => prev.filter(h => h.id !== hypothesisId)),
      // Cells - upsert logic already handles duplicates
      cell_updated: (cell) => setAchCells(prev => {
        const existing = prev.findIndex(c => c.hypothesisId === cell.hypothesisId && c.evidenceId === cell.evidenceId);
        if (existing >= 0) {
          return prev.map((c, i) => i === existing ? cell : c);
        }
        return [...prev, cell];
      }),
      // Notes - check for duplicates before adding
      note_created: (note) => setNotes(prev =>
        prev.some(n => n.id === note.id) ? prev : [...prev, note]
      ),
      note_updated: (note) => setNotes(prev => prev.map(n => n.id === note.id ? note : n)),
      note_deleted: ({ noteId }) => setNotes(prev => prev.filter(n => n.id !== noteId)),
      threat_assessment_updated: (ta) => setThreatAssessment(ta),
      assessment_status_changed: ({ status }) => {
        setAssessment(prev => ({ ...prev, status }));
        if (status === 'time_expired') {
          setShowTimeExpired(true);
        }
      },
      assessment_paused: () => setAssessment(prev => ({ ...prev, status: 'paused' })),
      assessment_resumed: () => setAssessment(prev => ({ ...prev, status: 'in_progress' })),
      assessment_submitted: ({ submittedBy, userName }) => {
        setAssessment(prev => ({ ...prev, status: 'submitted' }));
        // Show notification if another team member submitted
        if (submittedBy !== user?.id) {
          setNotificationMessage(`${userName || 'Un membre de l\'equip'} ha enviat l'exercici`);
          setShowNotification(true);
        }
      },
      // El servidor envia submittedBy també en abandonar, no abandonedBy: llegint
      // el nom equivocat sortia undefined, i qui abandonava rebia ell mateix
      // l'avís que «un membre de l'equip ha abandonat».
      assessment_abandoned: ({ submittedBy, userName }) => {
        setAssessment(prev => ({ ...prev, status: 'abandoned' }));
        if (submittedBy !== user?.id) {
          setNotificationMessage(`${userName || 'Un membre de l\'equip'} ha abandonat l'exercici`);
          setShowNotification(true);
        }
      },
      // El servidor recalcula el rellotge en desbloquejar i envia els valors nous:
      // cal aplicar-los, no només canviar l'estat, o el compte enrere seguiria amb
      // els vells i tornaria a disparar el temps esgotat immediatament.
      assessment_unlocked: ({ startedAt, pausedSeconds }) => {
        setAssessment(prev => ({
          ...prev,
          status: 'in_progress',
          ...(startedAt !== undefined ? { startedAt } : {}),
          ...(pausedSeconds !== undefined ? { pausedSeconds } : {})
        }));
        setNotificationMessage('L\'instructor ha desbloquejat l\'exercici');
        setShowNotification(true);
      },
      assessment_reset: () => {
        // Reload all data since everything was reset
        loadAssessment();
        setNotificationMessage('L\'instructor ha reiniciat l\'exercici');
        setShowNotification(true);
      }
    };

    const unsubscribers = Object.entries(handlers).map(([event, handler]) => on(event, handler));
    return () => unsubscribers.forEach(unsub => unsub());
  }, [connected, on, user?.id, loadAssessment]);

  // Actor handlers
  const handleAddActor = () => {
    setEditingActor(null);
    setShowActorForm(true);
  };

  const handleEditActor = (actor) => {
    setEditingActor(actor);
    setShowActorForm(true);
  };

  const handleSaveActor = async (actorData) => {
    try {
      if (editingActor) {
        // Optimistic update
        setActors(prev => prev.map(a =>
          a.id === editingActor.id ? { ...a, ...actorData } : a
        ));
        setShowActorForm(false);
        setEditingActor(null);

        await assessments.updateActor(id, editingActor.id, actorData);
      } else {
        // Optimistic update with temporary ID
        const tempId = `temp-${Date.now()}`;
        const tempActor = { id: tempId, ...actorData, createdAt: new Date().toISOString() };
        setActors(prev => [...prev, tempActor]);
        setShowActorForm(false);
        setEditingActor(null);

        const { data } = await assessments.createActor(id, actorData);
        // Replace temp with real actor, but check if socket already added it
        setActors(prev => {
          const alreadyExists = prev.some(a => a.id === data.id);
          if (alreadyExists) {
            return prev.filter(a => a.id !== tempId);
          }
          return prev.map(a => a.id === tempId ? data : a);
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error guardant actor');
      // Reload actors on error
      try {
        const actorsRes = await assessments.getActors(id);
        setActors(actorsRes.data);
      } catch (reloadErr) {
        // Ignore reload error
      }
    }
  };

  const handleDeleteActor = async (actorId) => {
    // Optimistic update - save for potential rollback
    const previousActors = actors;
    setActors(prev => prev.filter(a => a.id !== actorId));

    try {
      await assessments.deleteActor(id, actorId);
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant actor');
      // Rollback on error
      setActors(previousActors);
    }
  };

  // ACH handlers
  const handleAddHypothesis = async (data) => {
    // Auto-generate code (H1, H2, etc.)
    const nextNum = hypotheses.length + 1;
    const code = `H${nextNum}`;

    // Optimistic update with temporary ID
    const tempId = `temp-${Date.now()}`;
    const tempHypothesis = { id: tempId, code, ...data, score: 0, createdAt: new Date().toISOString() };
    setHypotheses(prev => [...prev, tempHypothesis]);

    try {
      const { data: newHypothesis } = await assessments.createHypothesis(id, { ...data, code });
      // Replace temp with real hypothesis, but check if socket already added it
      setHypotheses(prev => {
        // Check if real hypothesis already exists (added by socket event)
        const alreadyExists = prev.some(h => h.id === newHypothesis.id);
        if (alreadyExists) {
          // Just remove the temp, socket already added the real one
          return prev.filter(h => h.id !== tempId);
        }
        // Replace temp with real
        return prev.map(h => h.id === tempId ? newHypothesis : h);
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Error creant hipòtesi');
      // Rollback on error
      setHypotheses(prev => prev.filter(h => h.id !== tempId));
    }
  };

  const handleEditHypothesis = async (hypothesisId, data) => {
    // Optimistic update
    setHypotheses(prev => prev.map(h =>
      h.id === hypothesisId ? { ...h, ...data } : h
    ));

    try {
      await assessments.updateHypothesis(id, hypothesisId, data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error actualitzant hipòtesi');
      // Reload on error
      try {
        const achRes = await assessments.getACH(id);
        setHypotheses(achRes.data.hypotheses || []);
      } catch (reloadErr) {
        // Ignore reload error
      }
    }
  };

  const handleDeleteHypothesis = async (hypothesisId) => {
    // Optimistic update - save for potential rollback
    const previousHypotheses = hypotheses;
    setHypotheses(prev => prev.filter(h => h.id !== hypothesisId));
    // Also remove associated cells
    setAchCells(prev => prev.filter(c => c.hypothesisId !== hypothesisId));

    try {
      await assessments.deleteHypothesis(id, hypothesisId);
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant hipòtesi');
      // Rollback on error
      setHypotheses(previousHypotheses);
      // Reload cells to restore
      try {
        const achRes = await assessments.getACH(id);
        setAchCells(achRes.data.cells || []);
      } catch (reloadErr) {
        // Ignore reload error
      }
    }
  };

  const handleUpdateCell = async (cellData) => {
    try {
      // Optimistic update
      setAchCells(prev => {
        const existing = prev.findIndex(c =>
          c.hypothesisId === cellData.hypothesisId && c.evidenceId === cellData.evidenceId
        );
        if (existing >= 0) {
          return prev.map((c, i) => i === existing ? { ...c, rating: cellData.rating } : c);
        }
        return [...prev, {
          hypothesisId: cellData.hypothesisId,
          evidenceId: cellData.evidenceId,
          rating: cellData.rating
        }];
      });

      // Server update (socket will also emit but we already updated)
      await assessments.updateACHCell(id, cellData);
    } catch (err) {
      setError(err.response?.data?.message || 'Error actualitzant cel·la');
      // Reload cells on error to restore correct state
      try {
        const achRes = await assessments.getACH(id);
        setAchCells(achRes.data.cells || []);
      } catch (reloadErr) {
        // Ignore reload error
      }
    }
  };

  // Notes handlers
  const handleAddNote = async (data) => {
    // Optimistic update with temporary ID
    const tempId = `temp-${Date.now()}`;
    const tempNote = {
      id: tempId,
      ...data,
      createdBy: user?.id,
      createdAt: new Date().toISOString()
    };
    setNotes(prev => [...prev, tempNote]);

    try {
      const { data: newNote } = await assessments.createNote(id, data);
      // Replace temp with real note, but check if socket already added it
      setNotes(prev => {
        const alreadyExists = prev.some(n => n.id === newNote.id);
        if (alreadyExists) {
          return prev.filter(n => n.id !== tempId);
        }
        return prev.map(n => n.id === tempId ? newNote : n);
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Error creant nota');
      // Rollback on error
      setNotes(prev => prev.filter(n => n.id !== tempId));
    }
  };

  const handleEditNote = async (noteId, data) => {
    // Optimistic update
    setNotes(prev => prev.map(n =>
      n.id === noteId ? { ...n, ...data } : n
    ));

    try {
      await assessments.updateNote(id, noteId, data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error actualitzant nota');
      // Reload on error
      try {
        const notesRes = await assessments.getNotes(id);
        setNotes(notesRes.data);
      } catch (reloadErr) {
        // Ignore reload error
      }
    }
  };

  const handleDeleteNote = async (noteId) => {
    // Optimistic update - save for potential rollback
    const previousNotes = notes;
    setNotes(prev => prev.filter(n => n.id !== noteId));

    try {
      await assessments.deleteNote(id, noteId);
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant nota');
      // Rollback on error
      setNotes(previousNotes);
    }
  };

  // Threat Assessment handlers
  //
  // Memoritzada i amb l'error propagat: ThreatAssessmentForm l'auto-desa cada tres
  // segons d'inactivitat i l'espera per saber si ha d'anunciar «canvis desats» o
  // «canvis sense desar». Si aquí s'empassés l'excepció, l'indicador mentiria; i si
  // la funció es recreés a cada render, el temporitzador es reiniciaria sense parar.
  // La còpia per al rollback es pren dins del setState per no dependre de l'estat
  // actual a les dependències.
  const handleSaveThreatAssessment = useCallback(async (data) => {
    let anterior = null;
    setThreatAssessment(prev => {
      anterior = prev;
      return { ...prev, ...data };
    });

    try {
      await assessments.updateThreatAssessment(id, data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error guardant avaluació');
      setThreatAssessment(anterior);
      throw err;
    }
  }, [id]);

  // Submit and Abandon handlers
  const handleSubmit = async () => {
    try {
      await assessments.submit(id);
      setAssessment(prev => ({ ...prev, status: 'submitted' }));
    } catch (err) {
      setError(err.response?.data?.message || 'Error enviant exercici');
    }
  };

  const handleAbandon = async () => {
    try {
      await assessments.abandon(id);
      setAssessment(prev => ({ ...prev, status: 'abandoned' }));
    } catch (err) {
      setError(err.response?.data?.message || 'Error abandonant exercici');
    }
  };

  const handleTimeExpired = async () => {
    try {
      await assessments.timeExpired(id);
      setAssessment(prev => ({ ...prev, status: 'time_expired' }));
      setShowTimeExpired(true);
    } catch (err) {
      // Ignore errors - time expiration may already have been triggered by another team member
      if (err.response?.data?.code !== 'INVALID_STATUS') {
        setError(err.response?.data?.message || 'Error processant temps esgotat');
      }
    }
  };

  const handleFinalSubmit = async () => {
    try {
      await assessments.submitThreatAssessment(id);
      setAssessment(prev => ({ ...prev, status: 'submitted' }));
    } catch (err) {
      setError(err.response?.data?.message || 'Error enviant avaluació final');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-subtle">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-content-secondary">Carregant avaluació...</p>
        </div>
      </div>
    );
  }

  if (error && !assessment) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-subtle">
        <div className="text-center">
          <p className="text-error mb-4">{error}</p>
          <Link to="/dashboard" className="text-accent hover:text-primary-700">
            Tornar al Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-surface-muted">
      {/* Timer Bar */}
      <TimerBar
        timeLimitMinutes={assessment?.timeLimitMinutes}
        startedAt={assessment?.startedAt}
        pausedAt={assessment?.pausedAt}
        pausedSeconds={assessment?.pausedSeconds}
        status={assessment?.status}
        onSubmit={handleSubmit}
        onAbandon={handleAbandon}
        onTimeExpired={handleTimeExpired}
        isReadOnly={isReadOnly}
        isTeamBased={assessment?.isTeamBased}
      />

      {/* Header */}
      <div className="bg-surface border-b border-border px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/dashboard" className="text-content-muted hover:text-content-secondary">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <h1 className="text-lg font-semibold text-content">{assessment?.caseTitle}</h1>
              <p className="text-xs text-content-muted">{assessment?.projectTitle}</p>
            </div>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
              assessment?.status === 'in_progress' ? 'bg-accent-subtle text-accent' :
              assessment?.status === 'paused' ? 'bg-warning-subtle text-warning-text' :
              assessment?.status === 'submitted' ? 'bg-success-subtle text-success-text' :
              assessment?.status === 'graded' ? 'bg-info-subtle text-info-text' :
              assessment?.status === 'time_expired' ? 'bg-error-subtle text-error-text' :
              assessment?.status === 'abandoned' ? 'bg-surface-muted text-content' :
              'bg-surface-muted text-content'
            }`}>
              {assessment?.status === 'in_progress' ? 'En Progrés' :
               assessment?.status === 'paused' ? 'Pausat' :
               assessment?.status === 'submitted' ? 'Enviat' :
               assessment?.status === 'graded' ? 'Qualificat' :
               assessment?.status === 'time_expired' ? 'Temps Esgotat' :
               assessment?.status === 'abandoned' ? 'Abandonat' :
               assessment?.status}
            </span>
          </div>

          <div className="flex items-center space-x-3">
            {/* Active users */}
            {activeUsers.length > 0 && (
              <div className="flex items-center space-x-1">
                <div className="flex -space-x-2">
                  {activeUsers.slice(0, 3).map((u) => (
                    <div
                      key={u.userId}
                      className="w-7 h-7 rounded-full bg-accent border-2 border-white flex items-center justify-center text-white text-xs font-medium"
                      title={u.email}
                    >
                      {u.email?.[0]?.toUpperCase()}
                    </div>
                  ))}
                  {activeUsers.length > 3 && (
                    <div className="w-7 h-7 rounded-full bg-content-disabled border-2 border-surface flex items-center justify-center text-white text-xs">
                      +{activeUsers.length - 3}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Connection status */}
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />

            {/* Notes toggle */}
            <button
              onClick={() => setNotesOpen(!notesOpen)}
              className={`p-2 rounded-lg relative ${notesOpen ? 'bg-warning-subtle text-warning-text' : 'text-content-muted hover:bg-surface-muted'}`}
              title="Notes"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {notes.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {notes.length > 9 ? '9+' : notes.length}
                </span>
              )}
            </button>

            {/* Chat toggle */}
            <button
              onClick={() => setChatOpen(!chatOpen)}
              className={`p-2 rounded-lg ${chatOpen ? 'bg-accent-subtle text-accent-hover' : 'text-content-muted hover:bg-surface-muted'}`}
              title="Xat"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-error-subtle border-b border-error/30 px-4 py-2 flex items-center justify-between">
          <p className="text-sm text-error-text">{error}</p>
          <button onClick={() => setError('')} className="text-error hover:text-error-text">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Tabs and content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Tab navigation */}
          <div className="bg-surface border-b border-border px-4">
            <nav className="flex space-x-1">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-accent'
                      : 'border-transparent text-content-muted hover:text-content-secondary hover:border-border-strong'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'case' && (
              <div className="h-full overflow-y-auto p-6 bg-surface">
                <div className="max-w-4xl mx-auto">
                  {/* Briefing Content - Immersive narrative */}
                  {assessment?.briefingContent && (
                    <div className="mb-8">
                      <div className="flex items-center mb-4">
                        <svg className="w-6 h-6 mr-3 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <h2 className="text-xl font-bold text-content">Briefing del Cas</h2>
                      </div>
                      <div className="bg-surface-subtle rounded-lg p-6 border border-border">
                        <MarkdownViewer content={assessment.briefingContent} />
                      </div>
                    </div>
                  )}

                  {/* Instructions - Task directives */}
                  {assessment?.instructions && (
                    <div className="mb-6 p-5 bg-accent-subtle border border-accent/30 rounded-lg">
                      <h3 className="text-lg font-semibold text-content mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        Instruccions de l'Exercici
                      </h3>
                      <MarkdownViewer content={assessment.instructions} />
                    </div>
                  )}

                  {/* Case Description - Brief context if no briefing */}
                  {assessment?.caseDescription && !assessment?.briefingContent && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-content mb-3">Descripció del Cas</h3>
                      <MarkdownViewer content={assessment.caseDescription} />
                    </div>
                  )}

                  {/* Metadata section */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <div className="flex flex-wrap gap-4 text-sm">
                      {assessment?.difficulty && (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-content-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="text-content-muted">Dificultat:</span>
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                            assessment.difficulty === 'beginner' ? 'bg-success-subtle text-success-text' :
                            assessment.difficulty === 'intermediate' ? 'bg-warning-subtle text-warning-text' :
                            'bg-error-subtle text-error-text'
                          }`}>
                            {assessment.difficulty === 'beginner' ? 'Principiant' :
                             assessment.difficulty === 'intermediate' ? 'Intermedi' : 'Avançat'}
                          </span>
                        </div>
                      )}
                      {assessment?.timeLimitMinutes && (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-content-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-content-muted">Temps límit:</span>
                          <span className="ml-2 font-medium text-content">
                            {Math.floor(assessment.timeLimitMinutes / 60)}h {assessment.timeLimitMinutes % 60}min
                          </span>
                        </div>
                      )}
                      {assessment?.isTeamBased && (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-content-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-content-muted">Mode:</span>
                          <span className="ml-2 font-medium text-content">Equip</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* No content message */}
                  {!assessment?.briefingContent && !assessment?.caseDescription && !assessment?.instructions && (
                    <div className="text-center py-12 text-content-muted">
                      <svg className="w-12 h-12 mx-auto text-content-disabled mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p>No hi ha contingut de briefing per aquest cas.</p>
                      <p className="text-sm mt-2">Consulteu les evidències al panell dret per començar l'anàlisi.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'actors' && (
              <ActorPanel
                actors={actors}
                templates={actorTemplates}
                onAdd={handleAddActor}
                onEdit={handleEditActor}
                onDelete={handleDeleteActor}
                isReadOnly={isReadOnly}
              />
            )}
            {activeTab === 'ach' && (
              <ACHMatrix
                hypotheses={hypotheses}
                evidence={evidence}
                cells={achCells}
                onAddHypothesis={handleAddHypothesis}
                onEditHypothesis={handleEditHypothesis}
                onDeleteHypothesis={handleDeleteHypothesis}
                onUpdateCell={handleUpdateCell}
                isReadOnly={isReadOnly}
              />
            )}
            {activeTab === 'assessment' && (
              <ThreatAssessmentForm
                assessment={threatAssessment}
                actors={actors}
                hypotheses={hypotheses}
                evidence={evidence}
                achCells={achCells}
                onSave={handleSaveThreatAssessment}
                onFinalSubmit={handleFinalSubmit}
                isReadOnly={isReadOnly}
              />
            )}
          </div>
        </div>

        {/* Right: Evidence Panel */}
        <EvidencePanel
          evidence={evidence}
          collapsed={evidenceCollapsed}
          onToggle={() => setEvidenceCollapsed(!evidenceCollapsed)}
        />
      </div>

      {/* Actor Form Modal */}
      {showActorForm && (
        <ActorForm
          actor={editingActor}
          templates={actorTemplates}
          onSave={handleSaveActor}
          onCancel={() => {
            setShowActorForm(false);
            setEditingActor(null);
          }}
        />
      )}

      {/* Time Expired Modal */}
      {showTimeExpired && (
        <TimeExpiredModal onClose={() => setShowTimeExpired(false)} />
      )}

      {/* Notification Modal for team actions */}
      {showNotification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4 p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-subtle flex items-center justify-center">
              <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-content mb-2">Notificació</h3>
            <p className="text-content-secondary mb-6">{notificationMessage}</p>
            <button
              onClick={() => setShowNotification(false)}
              className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
            >
              D'acord
            </button>
          </div>
        </div>
      )}

      <AvisCopia visible={avisCopia} />

      {/* Floating Notes Panel */}
      {notesOpen && (
        <div className="fixed bottom-4 right-24 w-96 h-[32rem] bg-surface rounded-lg shadow-xl border border-border flex flex-col z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-warning bg-warning text-white rounded-t-lg">
            <h3 className="font-medium flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Notes ({notes.length})
            </h3>
            <button onClick={() => setNotesOpen(false)} className="hover:bg-warning p-1 rounded">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content - render NotesPanel in compact mode */}
          <div className="flex-1 overflow-hidden">
            <NotesPanel
              notes={notes}
              currentUserId={user?.id}
              onAdd={handleAddNote}
              onEdit={handleEditNote}
              onDelete={handleDeleteNote}
              isReadOnly={isReadOnly}
              compact={true}
            />
          </div>
        </div>
      )}

      {/* Chat Panel - Only for team-based assessments */}
      {assessment?.isTeamBased && (
        <ChatPanel
          assessmentId={id}
          isOpen={chatOpen}
          onToggle={() => setChatOpen(!chatOpen)}
        />
      )}
    </div>
  );
}

export default AssessmentWorkspacePage;
